import { useState, useEffect } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { getViews } from '../services/api';

const navigation = [
  {
    key: 'dashboard',
    href: '/',
    icon: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    key: 'events',
    href: '/events',
    icon: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    key: 'calendar',
    href: '/calendar',
    icon: (
      <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <rect x="8" y="11" width="2" height="2" fill="currentColor" />
        <rect x="11" y="11" width="2" height="2" fill="currentColor" />
        <rect x="14" y="11" width="2" height="2" fill="currentColor" />
      </svg>
    ),
  },
];

export const Sidebar = ({ isOpen, onClose, isMobile }) => {
  const { t, isRTL } = useLanguage();
  const [views, setViews] = useState([]);
  const [searchParams] = useSearchParams();
  const currentView = searchParams.get('view');

  useEffect(() => {
    fetchViews();
  }, []);

  const fetchViews = async () => {
    try {
      const response = await getViews();
      setViews(response.data || []);
    } catch (error) {
      console.error('Failed to load views:', error);
    }
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside
      className={`
        fixed ${isRTL ? 'right-0' : 'left-0'} top-16 h-[calc(100vh-4rem)]
        bg-white shadow-lg
        transition-transform duration-300 ease-in-out
        w-64 z-30
        ${isOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full')}
      `}
    >
      <nav className="h-full overflow-y-auto p-4 flex flex-col">
        <ul className="space-y-2 flex-1">
          {/* Main Navigation */}
          {navigation.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.href}
                end={item.href === '/'}
                onClick={handleLinkClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive && !currentView
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                {item.icon}
                <span>{item.label || t(item.key)}</span>
              </NavLink>
            </li>
          ))}

          {/* Views Section */}
          {views.length > 0 && (
            <>
              <li className="pt-4 pb-2">
                <div className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('views') || 'Views'}
                </div>
              </li>
              {views.map((view) => (
                <li key={view.id}>
                  <NavLink
                    to={`/?view=${view.id}`}
                    onClick={handleLinkClick}
                    className={() =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        currentView === view.id
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                  >
                    <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>
                      {view.view_name}
                      {view.filter_criteria?.activity && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({view.filter_criteria.activity})
                        </span>
                      )}
                    </span>
                  </NavLink>
                </li>
              ))}
            </>
          )}
        </ul>

        {/* Settings Section - Separated at Bottom */}
        <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
          <NavLink
            to="/settings"
            onClick={handleLinkClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`
            }
          >
            <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{t('settings')}</span>
          </NavLink>

          {/* API Documentation Link */}
          <NavLink
            to="/api-docs"
            onClick={handleLinkClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                isActive
                  ? 'text-blue-600 font-medium'
                  : 'text-gray-600 hover:text-blue-600'
              }`
            }
          >
            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>API Documentation</span>
          </NavLink>
        </div>
      </nav>
    </aside>
  );
};
